import { useMemo, useState } from 'react';
import { useAuth } from '../app/Auth';
import { Chip, Empty, ErrorNote, Icon, PageHeader, Segmented, Spinner } from '../app/ui';
import { callFn } from '../lib/api';
import { approveStepUp, stepUpPlan } from '../lib/stepup';
import { hoursLabel } from '../lib/format';
import { usePref } from '../lib/persist';
import { useApi } from '../lib/useApi';
import { WAITING_FILTERS, SNOOZE_CHOICES, filterWaiting, groupByProject } from '../lib/waiting';
import type { WaitingFilter } from '../lib/waiting';
import type { WaitingItem, WaitingResponse } from '../lib/types';
import { SessionStrip } from './SessionStrip';

function Row({ w, onDone }: { w: WaitingItem; onDone: () => Promise<void> }) {
  const { toast } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [snooze, setSnooze] = useState(24);
  const isApproval = w.item === 'approval';
  const evidence = (w.evidence ?? []).filter((e) => e && typeof e === 'object');

  async function run(label: string, action: string, payload: Record<string, unknown>) {
    setBusy(true);
    try { await callFn(action, payload); toast(label, 'ok'); await onDone(); }
    catch (e) { toast(e instanceof Error ? e.message : String(e), 'err'); }
    finally { setBusy(false); }
  }
  const stepUp = isApproval ? stepUpPlan(w) : null;
  const decide = (decision: 'approved' | 'denied' | 'snoozed') => {
    if (decision === 'approved' && stepUp) {
      setBusy(true);
      void approveStepUp(w, note.trim() || undefined)
        .then(async () => { toast('Elevation approved', 'ok'); await onDone(); })
        .catch((e) => toast(e instanceof Error ? e.message : String(e), 'err'))
        .finally(() => setBusy(false));
      return;
    }
    if (decision === 'approved' && w.irreversible && !window.confirm('This approval is marked irreversible. Approve it?')) return;
    const label = decision === 'approved' ? 'Approved' : decision === 'denied' ? 'Declined' : `Snoozed ${SNOOZE_CHOICES.find((s) => s.hours === snooze)?.label ?? ''}`;
    void run(label, 'approval_decide', { id: w.id, decision, snoozeHours: snooze, ...(note.trim() ? { note: note.trim() } : {}) });
  };
  const update = (label: string, patch: Record<string, unknown>) =>
    void run(label, 'initiative_update', { id: w.id, ...patch, ...(note.trim() ? { note: note.trim() } : {}) });

  return (
    <article className={`card wrow esc-${w.escalation ?? 'none'}`}>
      <button className="wrow-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className={`dot dot-${w.escalation ?? 'none'}`} title={w.escalation ?? ''} />
        {w.key ? <span className="mono key">{w.key}</span> : isApproval ? <Chip tone="orange">approval</Chip> : null}
        <span className="wtitle">{w.title}</span>
        <span className={`chev${open ? ' open' : ''}`}><Icon name="chevron" size={16} /></span>
      </button>
      <div className="row wrap gap muted small meta">
        {w.projectName ? <span>{w.projectName}</span> : null}
        <span>{isApproval ? w.kind ?? 'approval' : w.rec ?? w.kind ?? ''}</span>
        <span>· {hoursLabel(w.ageHours)}</span>
        {w.requestedBy ? <span>· from {w.requestedBy}</span> : null}
        {w.irreversible ? <Chip tone="orange" title="Cannot be undone">irreversible</Chip> : null}
        {w.snoozeUntil ? <span>· snooze ended</span> : null}
      </div>
      {stepUp ? (
        <div className="row wrap gap small meta">
          <Chip tone="orange">admin elevation</Chip>
          <span className="mono">{stepUp.actionClass}</span>
          <span>for {stepUp.minutes} min</span>
          <span>· requested by {w.requestedBy ?? 'an agent'}</span>
          <span className="muted">· approving asks for your passkey</span>
        </div>
      ) : null}
      {w.detail ? <p className={`detail${open ? ' full' : ''}`}>{w.detail}</p> : null}
      {open && evidence.length ? (
        <div className="evidence">
          <div className="muted small">Attached evidence</div>
          <ul>
            {evidence.map((e, n) => (
              <li key={n}>
                <Chip>{String(e.kind ?? 'note')}</Chip>{' '}
                {e.note ? <span>{String(e.note)}</span> : <span className="mono small">{JSON.stringify(e).slice(0, 300)}</span>}
                {e.by || e.at ? <span className="muted small"> — {String(e.by ?? '')} {e.at ? String(e.at).slice(0, 16).replace('T', ' ') : ''}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {open ? <input className="note" placeholder="Note (optional, saved with the decision)" value={note} onChange={(e) => setNote(e.target.value)} /> : null}
      <div className="actions">
        {isApproval ? (
          <>
            <button className="btn good" disabled={busy} onClick={() => decide('approved')}><Icon name="check" size={16} /> {stepUp ? 'Approve with passkey' : 'Approve'}</button>
            <button className="btn bad" disabled={busy} onClick={() => decide('denied')}><Icon name="x" size={16} /> Decline</button>
            <span className="snooze">
              <select aria-label="Snooze for" value={snooze} onChange={(e) => setSnooze(Number(e.target.value))}>
                {SNOOZE_CHOICES.map((s) => <option key={s.hours} value={s.hours}>{s.label}</option>)}
              </select>
              <button className="btn" disabled={busy} onClick={() => decide('snoozed')}>Snooze</button>
            </span>
          </>
        ) : (
          <>
            <button className="btn good" disabled={busy} onClick={() => update('Marked done', { status: 'done' })}>Mark done</button>
            <button className="btn" disabled={busy} onClick={() => update('Parked', { status: 'parked' })}>Park</button>
            <button className="btn" disabled={busy} onClick={() => update('Handed to the Assistant', { waitingOn: 'assistant' })}>Hand to Assistant</button>
          </>
        )}
        {busy ? <span className="spinner" /> : null}
      </div>
    </article>
  );
}

export function WaitingPage() {
  const { refreshMe } = useAuth();
  const q = useApi<WaitingResponse>('waiting', {}, { poll: 30000 });
  const [filter, setFilter] = usePref<WaitingFilter>('waiting.filter', 'all');
  const [grouped, setGrouped] = usePref<boolean>('waiting.group', false);
  const [query, setQuery] = useState('');
  const all = q.data?.items ?? [];
  const items = useMemo(() => filterWaiting(all, filter, query), [all, filter, query]);
  const reload = async () => { await q.reload(true); await refreshMe(); };

  return (
    <section className="page">
      <PageHeader title="Waiting on you">
        <button className="icon-btn" onClick={() => void reload()} aria-label="Refresh"><Icon name="refresh" /></button>
      </PageHeader>
      <div className="toolbar">
        <Segmented label="Show" value={filter} options={WAITING_FILTERS} onChange={setFilter} />
        <label className="check"><input type="checkbox" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} /> Group by project</label>
        <input className="search" type="search" placeholder="Search: key, words, project" value={query} onChange={(e) => setQuery(e.target.value)} />
        <span className="muted small count-note">{items.length === all.length ? `${all.length} waiting` : `${items.length} of ${all.length}`}
          {q.data && q.data.counts.escalate > 0 ? <> · <Chip tone="red">{q.data.counts.escalate} over 72 h</Chip></> : null}</span>
      </div>
      <ErrorNote text={q.error} />
      <SessionStrip />
      {q.loading && !q.data ? <Spinner label="Loading…" /> : null}
      {q.data && !items.length ? <Empty>{all.length ? 'Nothing matches.' : 'Nothing is waiting on you.'}</Empty> : null}
      {grouped ? groupByProject(items).map(([name, rows]) => (
        <div key={name} className="group">
          <h3 className="section-title">{name}<span className="count">{rows.length}</span></h3>
          <div className="stack">{rows.map((w) => <Row key={w.id} w={w} onDone={reload} />)}</div>
        </div>
      )) : <div className="stack">{items.map((w) => <Row key={w.id} w={w} onDone={reload} />)}</div>}
    </section>
  );
}
